import { useEffect, useMemo, useState } from "react";

import i18n from "@/i18n/i18n";
import { AnalyticsServiceV2 } from "@/services/features/AnalyticsServiceV2";
import {
  AnalyticsCardData,
  DateRange,
  storePerformanceService,
} from "@/services/StorePerformanceService";
import { formatDecimal } from "@/utils/number";
import { getBrowserTimezone } from "@/utils/storeTime";
import { useLatestRequestGuard } from "@/hooks/useLatestRequestGuard";

export const useCustomerSatisfaction = (
  storeId: number[] | null,
  dateRange?: DateRange,
): AnalyticsCardData => {
  const currentLanguage = i18n.resolvedLanguage || i18n.language;
  const browserTimezone = getBrowserTimezone();
  const [rawData, setRawData] = useState<{ average: number; numberOfShopperRating: number } | null>(
    null,
  );
  const beginRequest = useLatestRequestGuard();

  useEffect(() => {
    const isCurrent = beginRequest();
    if (!storeId || !dateRange) {
      setRawData(null);
      return;
    }

    const fetchSatisfaction = async () => {
      try {
        const analyticsService = AnalyticsServiceV2.getInstance();
        const response = await analyticsService.getShopperFeedbackAverage({
          storeIds: storeId,
          startDate: storePerformanceService.formatDate(dateRange.start, browserTimezone),
          endDate: storePerformanceService.formatDate(dateRange.end, browserTimezone),
        });

        const numberOfShopperRating = response?.data?.numberOfShopperRating ?? 0;
        const totalShopperRating = response?.data?.totalShopperRating ?? 0;

        if (numberOfShopperRating <= 0 || totalShopperRating <= 0) {
          if (isCurrent()) setRawData(null);
          return;
        }

        const average = totalShopperRating / numberOfShopperRating;
        if (isCurrent()) setRawData({ average, numberOfShopperRating });
      } catch (error) {
        console.error("Error fetching customer satisfaction:", error);
        if (isCurrent()) setRawData(null);
      }
    };

    fetchSatisfaction();
  }, [
    Array.isArray(storeId) ? storeId.join(",") : (storeId ?? null),
    browserTimezone,
    storePerformanceService.formatDate(dateRange?.start, browserTimezone),
    storePerformanceService.formatDate(dateRange?.end, browserTimezone),
  ]);

  return useMemo(() => {
    if (!rawData) {
      return {
        previousValue: null,
        isPositive: true,
        pillText: "",
        subtitle: i18n.t("common.analytics.noDataForTimeframe"),
        value: "--",
        rawValue: null,
      };
    }

    return {
      previousValue: null,
      value: formatDecimal(rawData.average, currentLanguage, {
        minimumFractionDigits: 1,
        maximumFractionDigits: 1,
      }),
      subtitle: i18n.t("common.analytics.fromShoppers", { count: rawData.numberOfShopperRating }),
      isPositive: rawData.average >= 4.0,
      pillText: null,
      rawValue: rawData.average,
    };
  }, [rawData, currentLanguage]);
};
